import styled, { css } from 'styled-components'

import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import Image from 'shared/components/Image'
import { CMSIntroductionLayout } from 'shared/enums/CMSIntroductionLayout'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, fonts, media } from 'shared/lib'
import { useHasCmsBanner } from 'shared/hooks/useHasCmsBanner'
import { useWindowSize } from 'shared/hooks/useWindowSize'
import { ArticleIntroduction } from 'shared/types/ArticleIntroduction'

const TP = 'editorial.components.ArticleIntroductionSection'

type ArticleIntroductionSectionProps = {
  section: ArticleIntroduction
}

export const ArticleIntroductionSection = ({ section }: ArticleIntroductionSectionProps) => {
  const { hasCmsBanner } = useHasCmsBanner()
  const { isLoading, isMobile } = useWindowSize()
  const hideFranchiseName = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_EDITORIALS_HIDE_FRANCHISE_NAME)

  if (!section?.sectionLayout?.flightClubIntroductionLayout) {
    return null
  }

  const fontColor = (() => {
    switch (section?.captionColor) {
      case 'red':
        return colors.FC2_RED
      case 'black':
        return colors.FC2_BLACK
      default:
        return colors.FC2_WHITE
    }
  })()
  const imageAssetUrl =
    isMobile && section?.mobileImageAsset?.url
      ? section?.mobileImageAsset?.url
      : section?.desktopImageAsset?.url
  const imageSeoDescription =
    isMobile && section?.mobileImageAsset?.seoDescription
      ? section?.mobileImageAsset?.seoDescription
      : section?.desktopImageAsset?.seoDescription
  const videoAssetUrl =
    isMobile && section?.mobileVideoAsset?.url
      ? section?.mobileVideoAsset?.url
      : section?.desktopVideoAsset?.url

  const IntroductionCopy = (
    <Container $flightClubIntroductionLayout={section?.sectionLayout?.flightClubIntroductionLayout}>
      {!hideFranchiseName && (
        <TypeContainer
          $flightClubIntroductionLayout={section?.sectionLayout?.flightClubIntroductionLayout}
          $fontColor={fontColor}
          data-qa="ArticleIntroductionSectionType"
          href="/editorial/fc-fridays"
        >
          {t(`${TP}.articleIntroType`, 'Flight Club Fridays')}
        </TypeContainer>
      )}
      <TitleContainer
        $flightClubIntroductionLayout={section?.sectionLayout?.flightClubIntroductionLayout}
        $fontColor={fontColor}
        data-qa="ArticleIntroductionSectionTitle"
      >
        {section?.caption}
      </TitleContainer>
    </Container>
  )

  switch (section.sectionLayout.flightClubIntroductionLayout) {
    case CMSIntroductionLayout.FullBleed:
      return (
        <FullBleedContainer $hasCmsBanner={hasCmsBanner} className="transparent-nav-overlay">
          <StyledImage
            $isLoadingWindow={isLoading}
            alt={imageSeoDescription || section?.caption}
            data-qa="ArticleIntroductionFullBleedImage"
            fill
            sizes="100vw"
            src={imageAssetUrl}
            priority={true}
          />
          {IntroductionCopy}
        </FullBleedContainer>
      )
    case CMSIntroductionLayout.FullBleedVideo:
      return (
        <FullBleedContainer $hasCmsBanner={hasCmsBanner} className="transparent-nav-overlay">
          <StyledVideo
            $isLoadingWindow={isLoading}
            autoPlay
            data-qa="ArticleIntroductionVideo"
            disablePictureInPicture
            loop
            muted
            playsInline
            preload="auto"
            src={`${videoAssetUrl}#t=0.5`}
          >
            <source src={videoAssetUrl} type="video/mp4" />
            <source src={videoAssetUrl} type="video/ogg" />
            {t(`${TP}.videoNotSupported`, 'Your browser does not support the video.')}
          </StyledVideo>
          {IntroductionCopy}
        </FullBleedContainer>
      )
    case CMSIntroductionLayout.Image:
      return (
        <>
          {IntroductionCopy}
          {section?.pictures?.[0]?.url && (
            <ImageContainer aria-label={section?.mediaAsset?.seoDescription || section?.caption}>
              <Image
                alt={section?.mediaAsset?.seoDescription || section?.caption}
                data-qa="ArticleIntroductionImage"
                src={section?.pictures?.[0]?.url}
                height={880}
                width={704}
              />
            </ImageContainer>
          )}
        </>
      )
    default:
      return IntroductionCopy
  }
}

const NAV_HEIGHT = 72
const Container = styled.div<{
  $flightClubIntroductionLayout:
    | CMSIntroductionLayout.FullBleed
    | CMSIntroductionLayout.FullBleedVideo
    | CMSIntroductionLayout.Image
    | CMSIntroductionLayout.Text
}>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ $flightClubIntroductionLayout }) => {
    switch ($flightClubIntroductionLayout) {
      case CMSIntroductionLayout.FullBleed:
      case CMSIntroductionLayout.FullBleedVideo:
        return css`
          height: 100vh;
          left: 0;
          position: absolute;
          top: 0;
          width: 100vw;
          z-index: 1;
        `
      case CMSIntroductionLayout.Image:
        return css`
          padding-left: 24px;
          padding-right: 24px;
          margin-bottom: 48px;
          margin-top: 48px;
          width: 100vw;
        `
      default:
        return css`
          padding-left: 24px;
          padding-right: 24px;
          margin-bottom: 120px;
          margin-top: 120px;
          width: 100vw;

          ${media.medium`
            margin-bottom: 240px;
          `}
        `
    }
  }};
`
const FullBleedContainer = styled.div<{ $hasCmsBanner: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 120px;

  ${media.medium`
    margin-bottom: 240px;
  `}

  ${({ $hasCmsBanner }) =>
    $hasCmsBanner
      ? css`
          height: calc(100vh - ${NAV_HEIGHT}px - 54px);
          ${media.medium`
            height: calc(100vh - ${NAV_HEIGHT}px - 39px);
          `};
        `
      : css`
          height: calc(100vh - ${NAV_HEIGHT}px);
          ${media.medium`
            height: calc(100vh - ${NAV_HEIGHT}px);
          `};
        `};
`
const ImageContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 120px;

  ${media.medium`
    margin-bottom: 240px;
  `}

  img {
    height: auto;
    object-fit: cover;
    width: calc(100vw - 48px);

    ${media.medium`
      width: 704px;
    `};
  }
`
const StyledImage = styled(Image)<{ $isLoadingWindow: boolean }>`
  display: block;
  object-fit: cover !important;
  width: 100vw !important;

  ${media.medium`
    height: 100vh !important;
    width: 100% !important;
  `};

  ${({ $isLoadingWindow }) =>
    $isLoadingWindow &&
    css`
      display: none;
    `};
`
const StyledVideo = styled.video<{ $isLoadingWindow: boolean }>`
  display: block;
  height: 100vh !important;
  left: 0;
  object-fit: cover !important;
  position: absolute;
  top: 0;
  width: 100vw !important;

  ${({ $isLoadingWindow }) =>
    $isLoadingWindow &&
    css`
      display: none;
    `};
`
const TitleContainer = styled.h1<{
  $flightClubIntroductionLayout:
    | CMSIntroductionLayout.FullBleed
    | CMSIntroductionLayout.FullBleedVideo
    | CMSIntroductionLayout.Image
    | CMSIntroductionLayout.Text
  $fontColor: string
}>`
  font-family: 'Ivar Headline'; /* stylelint-disable-line font-family-no-missing-generic-family-keyword */
  font-size: 24px;
  font-weight: 400;
  letter-spacing: -0.24px;
  line-height: 100%;
  margin: 0;
  max-width: 440px;
  text-align: center;
  text-transform: uppercase;
  text-wrap: balance;

  ${media.medium`
    font-size: 32px;
    letter-spacing: -0.32px;
  `}

  ${({ $flightClubIntroductionLayout, $fontColor }) => {
    switch ($flightClubIntroductionLayout) {
      case CMSIntroductionLayout.FullBleed:
      case CMSIntroductionLayout.FullBleedVideo:
        return css`
          color: ${$fontColor};
        `
      default:
        return css`
          color: ${colors.FC2_RED};
        `
    }
  }};
`
const TypeContainer = styled.a<{
  $flightClubIntroductionLayout:
    | CMSIntroductionLayout.FullBleed
    | CMSIntroductionLayout.FullBleedVideo
    | CMSIntroductionLayout.Image
    | CMSIntroductionLayout.Text
  $fontColor: string
}>`
  ${fonts.STANDARD};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  margin-bottom: 4px;
  text-decoration: none;
  text-transform: uppercase;

  ${({ $flightClubIntroductionLayout, $fontColor }) => {
    switch ($flightClubIntroductionLayout) {
      case CMSIntroductionLayout.FullBleed:
      case CMSIntroductionLayout.FullBleedVideo:
        return css`
          color: ${$fontColor};
        `
      default:
        return css`
          color: ${colors.FC2_RED};
        `
    }
  }};
`
